import { useState } from 'react';

import { useToasts } from '../../hooks/use-toasts';
import { copyTreeToNodes } from '../../utils/copy-tree-to-nodes';
import { nodemapToTree } from '../../utils/nodemap-to-tree';
import { FlatNode, NodeMap, Tree } from './types';
import { Data } from './use-mindmap-data';
import { Manipulation } from './use-mindmap-manipulation';
import { Selection } from './use-mindmap-selection';
import { Viewport } from './use-mindmap-viewport';

export type CopyCutPasteActions = {
  onCopyClick: () => void;
  onPasteClick: () => void;
  onCutClick: () => void;
  onUncutClick: () => void;
  copySelectable: boolean;
  pasteSelectable: boolean;
  cutSelectable: boolean;
  uncutSelectable: boolean;
};

export interface CopyCutPaste {
  copiedTrees: Tree[];
  copySelectedNodes: () => void;
  pasteNodes: (trees: Tree[], parentIds: string[], { keepIds }: { keepIds: boolean }) => NodeMap | void;
  revertCutting: () => void;
  cutSelectedNodes: () => void;
  getTreesOfNodeIds: (nodeIds: string[]) => Tree[];
  hasCutNodes: boolean;
  setHasCutNodes: React.Dispatch<React.SetStateAction<boolean>>;
  actions: CopyCutPasteActions;
}

type UseCopyCutPaste = { data: Data; selection: Selection; manipulation: Manipulation; viewport: Viewport };

export const useMindmapCopyCutPaste = ({
  data: { nodeMap, setNodeMap, rootNodeId },
  selection: { selectedNodeIds, setSelectedNodeIds, unselectNodes },
  manipulation: { removeNode },
  viewport: { moveIntoViewport },
}: UseCopyCutPaste): CopyCutPaste => {
  const { showToast } = useToasts();
  const [copiedTrees, setCopiedTrees] = useState<Tree[]>([]);
  const [hasCutNodes, setHasCutNodes] = useState<boolean>(false);

  const getTreesOfNodeIds = (nodeIds: string[]) => {
    return nodeIds.map((id) => nodemapToTree(nodeMap, ({ id: _id }) => _id === id)).filter((tree): tree is Tree => !!tree);
  };

  const copySelectedNodes = () => {
    if (nodeMap) setCopiedTrees(getTreesOfNodeIds(selectedNodeIds));
  };

  const cutSelectedNodes = () => {
    copySelectedNodes();
    removeNode(...selectedNodeIds);
    unselectNodes();
  };

  const revertCutting = () => {
    if (hasCutNodes) {
      pasteNodes(
        copiedTrees,
        copiedTrees.map(({ pid }) => pid).filter((pid): pid is string => !!pid),
        { keepIds: true },
      );
      setSelectedNodeIds(copiedTrees.map(({ id }) => id));
      setCopiedTrees([]);
      setHasCutNodes(false);
    }
  };

  const pasteNodes = (
    trees: Tree[],
    parentIds: string[],
    { keepIds }: { keepIds: boolean } = { keepIds: false },
  ): NodeMap | void => {
    if (nodeMap) {
      if (trees.length == 0) {
        return showToast({ severity: 'error', summary: 'Nothing to paste.' });
      }
      if (parentIds.length == 0) {
        return showToast({ severity: 'error', summary: 'Please select node(s) to paste into.' });
      }

      let newNodes: FlatNode[] = [];
      let idMapping: Record<string, string> = {};

      // remove trees that are inside of other trees
      trees = trees.filter((tree) => {
        // check if tree is inside of other tree
        const otherTrees = trees.filter(({ id }) => id !== tree.id);
        let otherTree: Tree | undefined = undefined;
        while ((otherTree = otherTrees.shift())) {
          // if the tree exists in an other tree filter it out
          if (otherTree.id == tree.id) return false;
          // if the tree doesn't exist in another tree look if the tree exists in its children
          if (otherTree.children) otherTrees.push(...otherTree.children);
        }
        return true;
      });

      if (parentIds.length == trees.length) {
        // copy mode 1: copy the nth copied nodes to the nth selected node
        parentIds.forEach((selectedNodeId, n) => {
          // prepare idMapping: the nth tree root node should have the nth selected node as a parent
          let pid;
          if ((pid = trees[n].pid)) idMapping[pid] = selectedNodeId;
          // create new flat nodes from the nth tree with new ids
          newNodes.push(...copyTreeToNodes([trees[n]], idMapping, { keepIds }));
        });
      } else {
        // copy mode 2: copy all copied nodes to all selected nodes
        parentIds.forEach((selectedNodeId) => {
          // prepare idMapping: for this selectedNodeId each tree root node should have the selectedNodeId as a parent
          trees.forEach((tree) => {
            if (tree.pid) idMapping[tree.pid] = selectedNodeId;
          });
          // create new flat nodes for this selectedNodeId with new ids
          newNodes.push(...copyTreeToNodes(trees, idMapping, { keepIds }));
        });
      }

      // convert list to object for node map state and update it
      let newNodeMap: NodeMap = structuredClone(nodeMap);
      newNodes.forEach((node) => (newNodeMap[node.id] = node));

      // delete nodemap positioning values
      Object.keys(newNodeMap).forEach((key) => {
        delete newNodeMap[key].x;
        delete newNodeMap[key].y;
      });

      // select all created sub-tree root nodes and move them into viewport
      const pastedIds = newNodes.filter(({ pid }) => pid && parentIds.includes(pid)).map(({ id }) => id);
      setNodeMap(newNodeMap);
      setSelectedNodeIds(pastedIds);
      moveIntoViewport(pastedIds);

      return newNodeMap;
    }
  };

  const selectedSomeNode = selectedNodeIds.length > 0;
  const copiedSomeNode = copiedTrees.length > 0;
  const selectedRootNode = rootNodeId && selectedNodeIds.includes(rootNodeId)
  const actions = {
    onCopyClick: () => copySelectedNodes(),
    onPasteClick: () => {
      pasteNodes(copiedTrees, selectedNodeIds, { keepIds: hasCutNodes });
      setHasCutNodes(false);
    },
    onCutClick: () => {
      cutSelectedNodes();
      setHasCutNodes(true);
    },
    onUncutClick: () => revertCutting(),
    copySelectable: selectedSomeNode && !selectedRootNode,
    pasteSelectable: selectedSomeNode && copiedSomeNode,
    cutSelectable: selectedSomeNode && !selectedRootNode,
    uncutSelectable: hasCutNodes,
  };

  return {
    copySelectedNodes,
    pasteNodes,
    revertCutting,
    cutSelectedNodes,
    getTreesOfNodeIds,
    copiedTrees,
    hasCutNodes,
    setHasCutNodes,
    actions,
  };
};
